<template>
  <!-- The standard opener for every guided walkthrough: names the flow, says
  what's about to happen and how long it takes. Copy comes from TOUR_INTROS in
  utils/setup/tourContent.js — add an entry there and a flow gets one of these. -->
  <BaseModal
    :card-width="hasSideColumn ? '720px' : '440px'"
    no-padding
    no-toolbar
    no-exit
    @close="$emit('close')"
  >
    <div
      class="flex"
      :class="hasSideColumn ? 'flex-row items-stretch' : 'flex-col'"
    >
      <!-- Copy column (full width when there's no side column) -->
      <div
        class="flex flex-col px-6 pt-4 pb-6"
        :class="hasSideColumn ? 'flex-1 min-w-0' : ''"
      >
        <!-- Header: kicker + close (close only when no video column) -->
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-1.5 text-text-subdued">
            <MouseIcon
              :width="14"
              :height="14"
            />
            <BaseText
              type="heading"
              size="overline"
              class="uppercase text-text-subdued"
            >
              {{ intro.kicker }}
            </BaseText>
          </div>
          <button
            v-if="!hasSideColumn"
            type="button"
            aria-label="Close"
            class="intro-close -mr-1.5 w-7 h-7 rounded-md flex items-center justify-center text-icon-normal hover:bg-neutral-50 hover:text-icon-muted focus:outline-none"
            @click="$emit('close')"
          >
            <TagRemoveIcon
              :width="16"
              :height="16"
            />
          </button>
        </div>

        <!-- Product badge -->
        <div
          class="mt-4 w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center"
          :class="isAnimatedBadge ? '' : badgeAccent.tile"
          @mouseenter="badgeHovered = true"
          @mouseleave="badgeHovered = false"
        >
          <AnimatedProductNavIcon
            v-if="isAnimatedBadge"
            :product="intro.icon"
            :size="40"
            :is-hovered="badgeHovered || autoplayBadge"
          />
          <component
            :is="icon"
            v-else-if="icon"
            :class="badgeAccent.icon"
          />
        </div>

        <BaseText
          type="display"
          size="xs"
          class="text-text-loud mt-4"
        >
          {{ interpolate(intro.title) }}
        </BaseText>
        <BaseText
          v-for="(paragraph, index) in bodyParagraphs"
          :key="`intro-p-${index}`"
          type="body"
          size="sm"
          class="text-text-muted"
          :class="index === 0 ? 'mt-2' : 'mt-1.5'"
        >
          {{ interpolate(paragraph) }}
        </BaseText>

        <!-- What happens, in order -->
        <div class="intro-steps relative mt-5 rounded-lg bg-neutral-10 border border-border-normal px-4 py-3.5">
          <div
            v-if="intro.steps.length > 1"
            class="intro-steps-line"
            aria-hidden="true"
          >
            <span
              class="intro-steps-line-flow"
              :class="accent.line"
            />
          </div>
          <ol class="flex flex-col gap-3.5">
            <li
              v-for="(step, index) in intro.steps"
              :key="step"
              class="flex items-center gap-3 ml-0"
            >
              <div
                class="relative z-10 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                :class="index === 0 ? accent.step : 'bg-white border border-border-normal'"
              >
                <BaseText
                  type="label"
                  size="xs"
                  :class="index === 0 ? 'text-white' : 'text-text-subdued'"
                >
                  {{ index + 1 }}
                </BaseText>
              </div>
              <BaseText
                type="body"
                size="sm"
                :class="index === 0 ? 'text-text-muted' : 'text-text-subdued'"
              >
                {{ interpolate(step) }}
              </BaseText>
            </li>
          </ol>
        </div>

        <div class="flex items-center justify-between gap-3 mt-6">
          <div class="flex items-center gap-1.5">
            <ClockIcon
              width="12"
              height="12"
              stroke="currentColor"
              class="text-icon-disabled"
            />
            <BaseText
              type="body"
              size="xs"
              class="text-text-subdued"
            >
              {{ intro.time }}
            </BaseText>
          </div>
          <BaseButton
            primary
            :forced-theme="intro.accent"
            :loading="loading"
            :disabled="loading"
            @click="start"
          >
            {{ ctaLabel || intro.ctaLabel }}
          </BaseButton>
        </div>
      </div>

      <!-- Video column: only when the intro defines a tutorial video
      (Instagram). Other walkthroughs stay single-column. -->
      <div
        v-if="hasSideColumn"
        class="intro-video-column relative flex-shrink-0 flex items-center justify-center"
      >
        <button
          type="button"
          aria-label="Close"
          class="intro-close absolute top-3 right-3 z-10 w-7 h-7 rounded-md flex items-center justify-center text-icon-normal hover:bg-neutral-50 hover:text-icon-muted focus:outline-none"
          @click="$emit('close')"
        >
          <TagRemoveIcon
            :width="16"
            :height="16"
          />
        </button>

        <!-- Autoplay muted loop, cover-cropped inside a square -->
        <div class="intro-video-frame">
          <video
            ref="nativeVideo"
            class="intro-video-cover"
            :src="intro.video.src"
            :poster="intro.video.poster"
            autoplay
            muted
            loop
            playsinline
          />
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import posthog from 'posthog-js'
import AnimatedProductNavIcon from '../globals/Icons/SidebarIcons/AnimatedProductNavIcon.vue'
import AdRankIcon from '../globals/Icons/SpyderModeIcons/AdRankIcon.vue'
import MonoInstagramIcon from '../globals/Icons/socials/mono/MonoInstagramIcon.vue'
import NewBoardIcon from '../globals/Icons/NewBoardIcon.vue'
import CreatePerformanceReportItemIcon from '../globals/Icons/SidebarIcons/dropdowns/CreatePerformanceReportItemIcon.vue'
import ComparativeIcon from '../globals/Icons/SidebarIcons/ComparativeIcon.vue'
import MCPIcon from '../globals/Icons/ApiIcons/MCPIcon.vue'
import MouseIcon from '../globals/Icons/MouseIcon.vue'
import ClockIcon from '../globals/Icons/ClockIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'

// Static badges by `icon` key. Keys that match a product sprite sheet render
// the animated product tile instead (see AnimatedProductNavIcon).
const INTRO_ICONS = {
  adRank: AdRankIcon,
  instagram: MonoInstagramIcon,
  newBoard: NewBoardIcon,
  topPerforming: CreatePerformanceReportItemIcon,
  comparison: ComparativeIcon,
  mcp: MCPIcon
}
// Lens report badges sit on a soft grey tile (Lens black has no light tint)
const SOFT_BADGES = ['topPerforming', 'comparison']
const SOFT_BADGE_ACCENT = { tile: 'bg-primary-black-10', icon: 'text-text-loud' }
const ANIMATED_PRODUCTS = ['spyder', 'library', 'discovery', 'briefs', 'lens']
// Product animation plays once as the modal opens, then a hover replays it
const BADGE_AUTOPLAY_DELAY_MS = 250
const BADGE_AUTOPLAY_DURATION_MS = 900

// Literal class strings so Tailwind's purge keeps them. `line` colors the
// steps connector animation (drawn with currentColor).
const ACCENTS = {
  red: { tile: 'bg-primary-red-10', icon: 'text-primary-red-100', step: 'bg-primary-red-100', line: 'text-primary-red-100' },
  blue: { tile: 'bg-primary-blue-10', icon: 'text-primary-blue-100', step: 'bg-primary-blue-100', line: 'text-primary-blue-100' },
  purple: { tile: 'bg-primary-purple-10', icon: 'text-primary-purple-100', step: 'bg-primary-purple-100', line: 'text-primary-purple-100' },
  black: { tile: 'bg-neutral-900', icon: 'text-white', step: 'bg-neutral-900', line: 'text-neutral-900' },
  green: { tile: 'bg-primary-green-10', icon: 'text-primary-green-100', step: 'bg-primary-green-100', line: 'text-primary-green-100' }
}

// Fallbacks for `{placeholder}` copy when no context value is available
const CONTEXT_DEFAULTS = {
  brandName: 'the brand'
}

export default {
  name: 'SetupTourIntroModal',
  components: {
    AnimatedProductNavIcon,
    MouseIcon,
    ClockIcon,
    TagRemoveIcon
  },
  props: {
    // One entry from TOUR_INTROS
    intro: {
      type: Object,
      required: true
    },
    // TOUR_INTROS key, for analytics
    introId: {
      type: String,
      required: true
    },
    // Values interpolated into `{placeholder}` copy (e.g. brandName)
    context: {
      type: Object,
      default: () => ({})
    },
    // Overrides intro.ctaLabel (e.g. the MCP intro's "Unlock API")
    ctaLabel: {
      type: String,
      default: null
    },
    // The CTA's action is in flight; Start is disabled until it settles
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      badgeHovered: false,
      autoplayBadge: false
    }
  },
  computed: {
    isAnimatedBadge () {
      return ANIMATED_PRODUCTS.includes(this.intro.icon)
    },
    hasSideColumn () {
      return !!this.intro.video
    },
    icon () {
      return INTRO_ICONS[this.intro.icon] || null
    },
    accent () {
      return ACCENTS[this.intro.accent] || ACCENTS.blue
    },
    badgeAccent () {
      if (SOFT_BADGES.includes(this.intro.icon)) return SOFT_BADGE_ACCENT
      return { tile: this.accent.tile, icon: this.accent.icon }
    },
    bodyParagraphs () {
      const body = this.intro.body
      if (!body) return []
      return Array.isArray(body) ? body.filter(Boolean) : [body]
    }
  },
  created () {
    posthog.capture('Setup Tour Intro Viewed', { tour_id: this.introId })
  },
  mounted () {
    if (this.isAnimatedBadge) {
      this.autoplayTimer = setTimeout(() => {
        this.autoplayBadge = true
        this.autoplayTimer = setTimeout(() => { this.autoplayBadge = false }, BADGE_AUTOPLAY_DURATION_MS)
      }, BADGE_AUTOPLAY_DELAY_MS)
    }
    // Muted autoplay is allowed by browsers, but play() can still reject
    if (this.hasSideColumn) {
      this.$nextTick(() => {
        const play = this.$refs.nativeVideo?.play()
        if (play && typeof play.catch === 'function') play.catch(() => {})
      })
    }
  },
  beforeDestroy () {
    clearTimeout(this.autoplayTimer)
  },
  methods: {
    interpolate (text) {
      if (!text) return ''
      return text.replace(/\{(\w+)\}/g, (match, key) => {
        const value = this.context[key]
        if (value !== undefined && value !== null && value !== '') return value
        return CONTEXT_DEFAULTS[key] !== undefined ? CONTEXT_DEFAULTS[key] : match
      })
    },
    start () {
      posthog.capture('Setup Tour Intro Started', { tour_id: this.introId })
      this.$emit('start')
    }
  }
}
</script>

<style scoped>
.intro-close {
  transition: background-color 150ms ease, color 150ms ease;
}

.intro-video-column {
  width: 300px;
  align-self: stretch;
  border-left: 1px solid theme('colors.neutral.100');
  @apply bg-white;
  border-radius: 0 12px 12px 0;
  overflow: hidden;
}

/* Square 1:1 frame, cover-cropped */
.intro-video-frame {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  overflow: hidden;
  @apply bg-white;
}

.intro-video-cover {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

/* Vertical connector: spans from the centre of the first badge to the centre
   of the last. Badges are 20px tall, list gap is 14px, container padding
   14px top/bottom, 16px left → badge centre sits 26px from the container's
   left edge. */
.intro-steps-line {
  position: absolute;
  left: 25px;
  top: 24px;
  bottom: 24px;
  width: 2px;
  border-radius: 9999px;
  @apply bg-neutral-100;
  overflow: hidden;
}

.intro-steps-line-flow {
  position: absolute;
  left: 0;
  right: 0;
  height: 45%;
  border-radius: 9999px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    currentColor 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: intro-line-flow 2.4s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

@keyframes intro-line-flow {
  0% {
    transform: translateY(-110%);
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateY(260%);
    opacity: 0;
  }
}
</style>
